/**
 * Signup Page
 *
 * Create a new seller account with email magic-link
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignupLinkRequestSchema } from "@/lib/api";
import { useSignupLink } from "@/lib/api/hooks";
import { useUiStore } from "@/lib/store";

export default function SignupPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const { mutate: signup, isPending } = useSignupLink();
  const addNotification = useUiStore((s) => s.addNotification);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: zodResolver(SignupLinkRequestSchema),
    defaultValues: {
      email: "",
      fullName: "",
      redirectUrl: `${window.location.origin}/dashboard`,
    },
  });

  const onSubmit = (data: any) => {
    signup(data, {
      onSuccess: () => {
        setSubmittedEmail(data.email);
        setIsSubmitted(true);
      },
      onError: (error) => {
        if (error.message.includes("already exists")) {
          setError("email", {
            type: "server",
            message: "This email is already registered. Try logging in instead.",
          });
        } else {
          addNotification(error.message, "error");
        }
      },
    });
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 text-center max-w-md w-full">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <span className="text-3xl">✓</span>
          </div>
          <h1 className="text-3xl font-bold text-ink mb-2">Check Your Email</h1>
          <p className="text-slate-600">
            We&apos;ve sent a sign-up link to <strong>{submittedEmail}</strong>
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-blue-700 mb-3">
            <strong>Next steps:</strong>
          </p>
          <ol className="text-sm text-blue-700 list-decimal list-inside space-y-2">
            <li>Check your email inbox (and spam folder)</li>
            <li>Click the link to verify your email</li>
            <li>You&apos;ll be logged in to your dashboard</li>
          </ol>
        </div>

        <p className="text-slate-600 text-sm mb-6">The link expires in 15 minutes for security.</p>

        <div className="space-y-3">
          <p className="text-slate-600 text-sm">Didn&apos;t receive an email?</p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="w-full px-6 py-3 border-2 border-accent text-accent rounded-lg hover:bg-blue-50 transition font-semibold"
          >
            Try Again
          </button>
        </div>

        <hr className="my-6" />

        <p className="text-slate-600 text-sm">
          Already have an account?{" "}
          <Link to="/auth/login" className="text-accent hover:underline font-semibold">
            Sign In
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
      <h1 className="text-3xl font-bold text-ink mb-2">Create Account</h1>
      <p className="text-slate-600 mb-8">Sign up to start selling boats</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-2">
            Full Name *
          </label>
          <input
            {...register("fullName")}
            type="text"
            id="fullName"
            placeholder="John Doe"
            disabled={isPending}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-slate-900 disabled:bg-slate-100 ${
              errors.fullName ? "border-red-500" : "border-ash"
            }`}
          />
          {errors.fullName && (
            <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
            Email *
          </label>
          <input
            {...register("email")}
            type="email"
            id="email"
            placeholder="you@example.com"
            disabled={isPending}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-slate-900 disabled:bg-slate-100 ${
              errors.email ? "border-red-500" : "border-ash"
            }`}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50 transition font-semibold mt-6"
        >
          {isPending ? "Sending..." : "Send Sign-up Link"}
        </button>
      </form>

      <hr className="my-6" />

      <p className="text-slate-600 text-sm text-center">
        Already have an account?{" "}
        <Link to="/auth/login" className="text-accent hover:text-accent-dark font-semibold">
          Sign In
        </Link>
      </p>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          <strong>🔒 Passwordless:</strong> We send you a secure magic-link via email. No password
          needed!
        </p>
      </div>
    </div>
  );
}
